import * as React from "react";
import { ApiError } from "@/api/client";
import { ConfirmModal } from "@/components/ui/modal";
import type { useAction } from "@/lib/useAsync";

/**
 * The worker asking whether a loss is meant: a 409 `needs_confirmation`,
 * answered by the same save sent again with `allowBelowCost`.
 *
 * Shared by every screen that can put a package below cost -- a package's
 * price and discount (Products), a coupon (Coupons) and the crypto discount
 * (Settings) -- so the question reads the same wherever it is asked.
 */
function isBelowCostQuestion(err: unknown): err is ApiError {
  return err instanceof ApiError && err.code === "needs_confirmation";
}

type Action = Pick<ReturnType<typeof useAction>, "run">;

/**
 * A write, asked twice when it has to be.
 *
 * `ask(action, write, after)` runs `write` inside `action`. A below-cost
 * question is not an error to print: it opens the dialog, and confirming sends
 * the same write again with the loss allowed. `after` runs once the write has
 * been answered -- with the answer, or null when it was refused -- and never
 * while the question is open, because nothing was written.
 */
export function useBelowCostQuestion() {
  const [loss, setLoss] = React.useState<{ message: string; allow: () => void } | null>(null);

  const ask = React.useCallback(
    <T,>(
      action: Action,
      write: (allowBelowCost: boolean) => Promise<T>,
      after: (answer: T | null) => void,
    ): Promise<void> => {
      async function attempt(allowBelowCost: boolean): Promise<void> {
        const written: { answer: T | null; asked: boolean } = { answer: null, asked: false };
        await action.run(async () => {
          try {
            written.answer = await write(allowBelowCost);
            return written.answer;
          } catch (err) {
            if (isBelowCostQuestion(err)) {
              written.asked = true;
              setLoss({ message: err.message, allow: () => void attempt(true) });
              return undefined;
            }
            throw err;
          }
        });
        if (!written.asked) after(written.answer);
      }
      return attempt(false);
    },
    [],
  );

  const dismiss = React.useCallback(() => setLoss(null), []);

  /** For `<BelowCostConfirm>`, with the `busy` the screen's actions share. */
  const question = {
    message: loss?.message ?? null,
    onClose: dismiss,
    onConfirm: () => {
      if (!loss) return;
      setLoss(null);
      loss.allow();
    },
  };
  return { ask, dismiss, question };
}

export function BelowCostConfirm({
  message,
  busy,
  onConfirm,
  onClose,
}: {
  /** The worker's sentence; null keeps the dialog closed. */
  message: string | null;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <ConfirmModal
      open={message !== null}
      title="Sell below cost?"
      confirmLabel="Save at a loss"
      busy={busy}
      onClose={onClose}
      body={
        <>
          <p>{message}</p>
          <p className="mt-2">Save it anyway only if the loss is intended -- a launch offer, say.</p>
        </>
      }
      onConfirm={onConfirm}
    />
  );
}
